import React, { useState } from 'react';
import { Box } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { logError } from 'utils/logger';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { BoardColumn } from './BoardColumn';
import { BoardProps, BoardColumnEntity, MoveCardEventParams } from './types';

export function Board<T>({ columns: initialColumns, renderCard, renderColumnHeader, onMoveCard }: BoardProps<T>) {
  const classes = useStyles({ columnsAmount: initialColumns.length });

  const [columns, setColumns] = useState<BoardColumnEntity<T>[]>(initialColumns);

  const handleMoveCard = (params: MoveCardEventParams) => {
    const { toIndex, itemIndex, toColumnId, fromColumnId } = params;

    setColumns(prev => {
      const newCols = [...prev];
      const fromCol = newCols.find(({ id }) => id === fromColumnId)!;
      const toCol = newCols.find(({ id }) => id === toColumnId)!;
      const [moved] = fromCol.cards.splice(itemIndex, 1);

      if (moved && typeof moved === 'object' && 'modifiedDateTime' in moved) {
        moved.modifiedDateTime = new Date().toISOString();
      }

      toCol.cards.splice(toIndex, 0, moved);
      return [...newCols];
    });

    const column = columns.find(({ id }) => id === params.toColumnId);
    const cardEntity = (column?.cards ?? [])[params.toIndex];

    onMoveCard({ ...params, cardEntity }).catch(error => {
      logError('Failed to move card:', error);
    });
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box className={classes.board}>
        {columns.map(({ id, ...other }) => (
          <BoardColumn<T>
            id={id}
            key={id}
            {...other}
            renderCard={renderCard}
            onMoveCard={handleMoveCard}
            renderColumnHeader={renderColumnHeader}
          />
        ))}
      </Box>
    </DndProvider>
  );
}

const useStyles = makeStyles<Theme, { columnsAmount: number }>({
  board: {
    gap: '0.5rem',
    height: '100%',
    display: 'flex',
    overflow: 'auto',
  },
});
